import type { DesktopControlLease, DesktopControlOwner } from '@/tauri/rpa';
import { logger } from '@/utils/logger/logger';

import {
  holdDesktopControlLease,
  type DesktopControlLeaseHolder,
} from './desktop-control-lease-holder';

export const DESKTOP_CONTROL_APPROVAL_RESERVATION_TIMEOUT_MS = 30_000;

interface CreateDesktopControlApprovalReservationArgs {
  approvalId: string;
  toolCallId: string;
  lease: DesktopControlLease;
  owner: DesktopControlOwner;
  timeoutMs?: number;
}

export interface DesktopControlApprovalReservation {
  approvalId: string;
  toolCallId: string;
  lease: DesktopControlLease;
  owner: DesktopControlOwner;
  holder: DesktopControlLeaseHolder;
}

interface StoredDesktopControlApprovalReservation extends DesktopControlApprovalReservation {
  timeoutId: ReturnType<typeof setTimeout>;
}

const reservations = new Map<string, StoredDesktopControlApprovalReservation>();

const stopStoredReservation = (
  reservation: StoredDesktopControlApprovalReservation
): void => {
  clearTimeout(reservation.timeoutId);
  reservation.holder.stop();
};

const deleteStoredReservation = (
  toolCallId: string
): StoredDesktopControlApprovalReservation | null => {
  const reservation = reservations.get(toolCallId);
  if (!reservation) return null;
  reservations.delete(toolCallId);
  clearTimeout(reservation.timeoutId);
  return reservation;
};

const expireReservation = (
  toolCallId: string,
  reservation: StoredDesktopControlApprovalReservation
): void => {
  if (reservations.get(toolCallId) !== reservation) return;
  reservations.delete(toolCallId);
  reservation.holder.stop();
  logger.debug('[desktop-control] Approval lease reservation expired', {
    approval_id: reservation.approvalId,
    tool_call_id: reservation.toolCallId,
    lease_id: reservation.lease.leaseId,
  });
};

export const createDesktopControlApprovalReservation = ({
  approvalId,
  toolCallId,
  lease,
  owner,
  timeoutMs = DESKTOP_CONTROL_APPROVAL_RESERVATION_TIMEOUT_MS,
}: CreateDesktopControlApprovalReservationArgs): DesktopControlApprovalReservation => {
  const existing = reservations.get(toolCallId);
  if (existing) {
    reservations.delete(toolCallId);
    stopStoredReservation(existing);
  }

  const holder: DesktopControlLeaseHolder = holdDesktopControlLease({
    leaseId: lease.leaseId,
    owner,
    onRefreshFailure: (error) => {
      const current = reservations.get(toolCallId);
      if (!current || current.holder !== holder) return;
      reservations.delete(toolCallId);
      stopStoredReservation(current);
      logger.warn('[desktop-control] Approval lease reservation lost ownership', {
        approval_id: approvalId,
        tool_call_id: toolCallId,
        lease_id: lease.leaseId,
        error: error instanceof Error ? error.message : String(error),
      });
    },
  });

  const reservation: StoredDesktopControlApprovalReservation = {
    approvalId,
    toolCallId,
    lease,
    owner,
    holder,
    timeoutId: setTimeout(() => expireReservation(toolCallId, reservation), timeoutMs),
  };
  reservations.set(toolCallId, reservation);
  return {
    approvalId,
    toolCallId,
    lease,
    owner,
    holder,
  };
};

export const consumeDesktopControlApprovalReservation = (
  toolCallId: string
): DesktopControlApprovalReservation | null => {
  const reservation = deleteStoredReservation(toolCallId);
  if (!reservation) return null;
  return {
    approvalId: reservation.approvalId,
    toolCallId: reservation.toolCallId,
    lease: reservation.lease,
    owner: reservation.owner,
    holder: reservation.holder,
  };
};

export const releaseDesktopControlApprovalReservation = async (
  toolCallId: string,
  context: Record<string, unknown> = {}
): Promise<void> => {
  const reservation = deleteStoredReservation(toolCallId);
  if (!reservation) return;
  try {
    await reservation.holder.release();
  } catch (error) {
    logger.warn('[desktop-control] Failed to release approval lease reservation', {
      ...context,
      approval_id: reservation.approvalId,
      tool_call_id: reservation.toolCallId,
      lease_id: reservation.lease.leaseId,
      error: error instanceof Error ? error.message : String(error),
    });
  }
};

export const releaseDesktopControlApprovalReservationsForAgentRun = async (
  agentRunId: string,
  context: Record<string, unknown> = {}
): Promise<void> => {
  const matchingToolCallIds = [...reservations.values()]
    .filter((reservation) => reservation.owner.agentRunId === agentRunId)
    .map((reservation) => reservation.toolCallId);

  await Promise.all(
    matchingToolCallIds.map((toolCallId) =>
      releaseDesktopControlApprovalReservation(toolCallId, context)
    )
  );
};

export const __resetDesktopControlApprovalReservationsForTests = (): void => {
  for (const reservation of reservations.values()) {
    stopStoredReservation(reservation);
  }
  reservations.clear();
};

export const __getDesktopControlApprovalReservationCountForTests = (): number =>
  reservations.size;
